'use client';

/**
 * **Whether an attribute surface is holding state the commit path could not
 * store** (VALE-349) — the signal a drawer stops and asks on.
 *
 * ## Why a context rather than a prop
 *
 * The same reason `LeadInlineEditingProvider` is one: the grids are not the
 * host's children in any useful sense. A surface renders **one
 * `useAttributeCommit` per `rows` block** (`AttributeSections` mounts an
 * `AttributeGrid` per block, and each grid owns its own commit state), so "does
 * this drawer have unsaved state" is an aggregate over an unknown number of
 * grids the host never named — and on the enquiries drawer the rail arrives as
 * an opaque `ReactNode` the host built, so there is no prop path to the card
 * inside it at all. Threading a callback would mean widening every level
 * between; a provider the grids report into costs one wrapper.
 *
 * ## Absent provider = no-op, deliberately
 *
 * `/enquiries/{id}` is a **page**: nothing there closes, so nothing needs
 * guarding and it supplies no provider. A Storybook render or a card-level spec
 * is in the same position. {@link useAttributeUnsavedReporter} therefore returns
 * a no-op rather than throwing — the `useLeadInlineEditing` shape, and for the
 * same reason: a hook that throws outside its provider makes the component
 * unmountable anywhere else.
 *
 * ## It reports synchronously, and that is load-bearing
 *
 * Radix's dismissable layer detects an outside click on `pointerdown` and
 * dismisses inside that same dispatch. A refusal produced by the blur that click
 * causes therefore has to reach the host **before** React re-renders — so the
 * chain from {@link useAttributeCommit}'s `onUnsavedChange` through here to the
 * host's `setIsDirty` is all synchronous, and `useUnsavedChangesGuard` reads a
 * ref rather than its own state when it decides whether to intercept. Hang any
 * step of that *reporting* on an effect and the drawer closes over the very
 * refusal it is meant to catch.
 *
 * (The one effect below is not in that chain: it refreshes the mirror of the
 * host's own callback between renders, which is always current by the time a
 * gesture runs.)
 */

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject
} from 'react';

/** Reports one grid's unsaved state, keyed by an id stable for its lifetime. */
export type AttributeUnsavedReporter = (id: string, unsaved: boolean) => void;

/**
 * What one grid lets the surface do to it (VALE-1274). Registered under the
 * same id the reporters use, and withdrawn by registering `null` on unmount.
 */
export interface AttributeGridControl {
  /** {@link AttributeCommit.flush} — commit whatever editor this grid has open. */
  flush: () => boolean;
}

/**
 * The whole channel between a grid and its surface. One object rather than
 * three contexts: a grid needs all of it, the surface supplies all of it, and
 * splitting them would mean three providers to keep in step.
 */
export interface AttributeSurfaceChannel {
  report: AttributeUnsavedReporter;
  /** Reports one grid's open-but-uncommitted draft — see the provider's prop. */
  reportUncommitted: AttributeUnsavedReporter;
  register: (id: string, control: AttributeGridControl | null) => void;
}

/**
 * **What a host holds on to.** Assigned into the `controlRef` it passes the
 * provider, because the host renders the provider and so is *outside* it — the
 * same imperative shape, and for the same reason, as
 * {@link focusOpenAttributeEditor}: the caller has no handle on a control
 * several components down inside a grid it did not render.
 */
export interface AttributeSurfaceControl {
  /**
   * Commit every open editor on this surface. `false` means one of them was
   * refused and is now showing the reason, so the caller must not submit.
   */
  flushOpenEditors: () => boolean;
}

const noop: AttributeUnsavedReporter = () => {};

const AttributeSurfaceContext = createContext<AttributeSurfaceChannel>({
  report: noop,
  reportUncommitted: noop,
  register: () => {}
});

export function AttributeUnsavedStateProvider({
  onUnsavedChange,
  onUncommittedDraftChange,
  controlRef,
  children
}: {
  /** Fires only on a transition, with the aggregate over every grid inside. */
  onUnsavedChange?: (unsaved: boolean) => void;
  /**
   * **Whether any grid inside is holding an open editor whose draft the reader
   * has changed** (VALE-1274) — the aggregate of
   * `useAttributeCommit`'s `onUncommittedDraftChange`, and a *separate*
   * question from {@link onUnsavedChange}: this one is "is there anything to
   * save", not "would closing lose something the commit path could not store".
   *
   * A buffered create surface needs it because its `Save` is disabled until a
   * row commits, and a popover row commits when it is dismissed — so without
   * it the reader's click on `Save` is the one gesture that cannot reach the
   * button. Deliberately **not** wired into the leave guard: that guard's
   * behaviour (VALE-60, VALE-164) is settled and is not this ticket's to move.
   */
  onUncommittedDraftChange?: (holding: boolean) => void;
  /**
   * Assigned {@link AttributeSurfaceControl} while mounted, `null` after.
   * Supply it from a surface with a `Save` button; omit it everywhere else.
   */
  controlRef?: RefObject<AttributeSurfaceControl | null>;
  children: ReactNode;
}) {
  /*
    Refs, not state: this provider renders nothing that depends on the answer,
    and re-rendering the whole subtree on every save would be a cost paid by
    every surface for a fact only the host's close handler reads.
  */
  const holdingRef = useRef<Set<string>>(new Set());
  const lastRef = useRef(false);
  const uncommittedRef = useRef<Set<string>>(new Set());
  const lastUncommittedRef = useRef(false);
  const controlsRef = useRef<Map<string, AttributeGridControl>>(new Map());
  /*
    Mirrored in an effect rather than assigned during render — the host's
    callback is an inline arrow, so it is a new identity every render and cannot
    be a dependency of `report`. Effects flush before the next user gesture, so
    the mirror is always current by the time a commit reads it.
  */
  const onUnsavedChangeRef = useRef(onUnsavedChange);
  const onUncommittedDraftChangeRef = useRef(onUncommittedDraftChange);
  useEffect(() => {
    onUnsavedChangeRef.current = onUnsavedChange;
    onUncommittedDraftChangeRef.current = onUncommittedDraftChange;
  });

  const report = useCallback<AttributeUnsavedReporter>((id, unsaved) => {
    if (unsaved) holdingRef.current.add(id);
    else holdingRef.current.delete(id);
    const next = holdingRef.current.size > 0;
    if (next === lastRef.current) return;
    lastRef.current = next;
    onUnsavedChangeRef.current?.(next);
  }, []);

  const reportUncommitted = useCallback<AttributeUnsavedReporter>(
    (id, holding) => {
      if (holding) uncommittedRef.current.add(id);
      else uncommittedRef.current.delete(id);
      const next = uncommittedRef.current.size > 0;
      if (next === lastUncommittedRef.current) return;
      lastUncommittedRef.current = next;
      onUncommittedDraftChangeRef.current?.(next);
    },
    []
  );

  const register = useCallback(
    (id: string, control: AttributeGridControl | null) => {
      if (control) controlsRef.current.set(id, control);
      else controlsRef.current.delete(id);
    },
    []
  );

  /*
    **Every grid is flushed, not just up to the first refusal**: a surface can
    hold more than one, and stopping early would commit some rows and leave
    others sitting in a draft the reader believes they saved. The verdict is the
    AND over all of them.

    Assigned in an effect rather than during render — unlike the reporting
    above, this one is only ever *read* from an event handler, and effects have
    flushed long before the reader can press anything.
  */
  useEffect(() => {
    if (!controlRef) return;
    controlRef.current = {
      flushOpenEditors: () =>
        Array.from(controlsRef.current.values()).reduce(
          (accepted, control) => control.flush() && accepted,
          true
        )
    };
    return () => {
      controlRef.current = null;
    };
  }, [controlRef]);

  const channel = useMemo<AttributeSurfaceChannel>(
    () => ({ report, reportUncommitted, register }),
    [register, report, reportUncommitted]
  );

  return (
    <AttributeSurfaceContext value={channel}>
      {children}
    </AttributeSurfaceContext>
  );
}

/**
 * The grid's end of the channel. Safe to call outside a provider — see the file
 * header; every member is a no-op there.
 */
export function useAttributeSurfaceChannel(): AttributeSurfaceChannel {
  return use(AttributeSurfaceContext);
}

/**
 * **Put the reader back in the field the guard stopped them leaving.**
 *
 * *Cancel* on the unsaved-changes dialog means "return me to the field", and by
 * then the editor has been blurred — by the outside click that started the
 * close, or by the dialog taking focus — so nothing restores it on its own:
 * Radix returns focus to whatever was active when the dialog mounted, which is
 * the drawer body.
 *
 * It queries rather than holding a ref because the caller is the *host*, which
 * has no handle on a control several components down inside a grid it did not
 * render. `data-attribute-open-editor` is the cell's own marker, set by
 * {@link AttributeValueCell}; at most one cell carries it per surface, since one
 * commit path holds one open field.
 *
 * Best-effort by design: a guard raised by a save still in flight has no open
 * editor to return to, and doing nothing is the right answer there.
 */
export function focusOpenAttributeEditor(root: ParentNode = document) {
  const cell = root.querySelector('[data-attribute-open-editor="true"]');
  const focusable = cell?.querySelector<HTMLElement>(
    'input, textarea, [contenteditable="true"], button, [role="combobox"]'
  );
  focusable?.focus();
}

/**
 * **Whether an attribute editor is open inside `root`** (VALE-1505), what a
 * host's `Escape` handler asks before letting its sheet dismiss.
 *
 * `AttributeGrid` reports `onEditingChange` per grid, and a host with one grid
 * threads it into its sheet's `onEscapeKeyDown` so a keypress meant for the
 * field does not also close the drawer. A host composing several records, the
 * Discovery sheet's assets column, the Title Search Review's no-changes card,
 * has no single callback to thread, and two of them shipped without one:
 * `Escape` in a `Notes` field closed the whole sheet. Reading the same marker
 * {@link focusOpenAttributeEditor} reads gives every host one answer with no
 * plumbing. Synchronous against the DOM, so it is true inside the keydown that
 * is about to dismiss.
 *
 * **Scope it.** The `document` default answers for the whole page, so an editor
 * left open BEHIND a sheet would hold the sheet's `Escape` hostage. A sheet
 * passes its own content node, and {@link escapeTargetHasOpenAttributeEditor}
 * derives it from the keypress for hosts that have no ref to hand.
 *
 * **The marker is the unified cell's, not every row's.** `AttributeValueCell`
 * sets it; the `legacyChrome` path skips that cell entirely, and `render` rows
 * and live booleans never set it. Both current callers are unified surfaces. A
 * guard added to a `legacyChrome` host would be a silent no-op, so check the
 * host's chrome before reaching for this.
 */
export function hasOpenAttributeEditor(root: ParentNode = document): boolean {
  return root.querySelector('[data-attribute-open-editor="true"]') !== null;
}

/**
 * {@link hasOpenAttributeEditor}, scoped to the dismissable layer the keypress
 * happened in.
 *
 * Radix hands `onEscapeKeyDown` a document-level event, so the host would
 * otherwise have to restate the selector for its own content node. Walking up
 * from the focused element to the nearest `[role="dialog"]` gets there without
 * a ref, and falls back to the whole document only when the keypress came from
 * outside any dialog, where the unscoped answer is the right one anyway.
 */
export function escapeTargetHasOpenAttributeEditor(event: {
  target: EventTarget | null;
}): boolean {
  const target = event.target;
  const layer =
    target instanceof Element ? target.closest('[role="dialog"]') : null;
  return hasOpenAttributeEditor(layer ?? document);
}
